/**
 * BorgApiClient — pure class, no React dependency.
 *
 * This is the ONLY place in the codebase where API URL routing based on
 * borg version lives. All components call methods on this class; none of
 * them construct URLs or check borg_version directly.
 *
 * v1 repos  →  /api/...       (existing routes, unchanged)
 * v2 repos  →  /api/v2/...    (new Borg 2 routes)
 */

import axios from 'axios'
import { BASE_PATH } from '@/utils/basePath'
import type { Repository } from '@/types'
import { isV2Repo } from '@/utils/repoCapabilities'
import { getApiBaseUrl } from '../remoteBackends/gateway'
import { getActiveBackendTarget } from '../remoteBackends/storage'
import {
  attachBackendTargetAccessHeaders,
  BACKEND_TARGET_ID_CONFIG_KEY,
  clearAccessToken,
  type BackendTargetRequestConfig,
} from '../authHeaders'
import type { AxiosProgressEvent, InternalAxiosRequestConfig } from 'axios'

export const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || `${BASE_PATH}/api`,
  headers: { 'Content-Type': 'application/json' },
})

function isOnLoginRoute(): boolean {
  const loginPath = `${BASE_PATH}/login`
  return window.location.pathname === loginPath || window.location.pathname === `${loginPath}/`
}

// Mirror the shared auth interceptor so this client also attaches tokens.
httpClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const target = getActiveBackendTarget()
  const targetConfig = config as BackendTargetRequestConfig
  targetConfig.baseURL = getApiBaseUrl(target)
  targetConfig[BACKEND_TARGET_ID_CONFIG_KEY] = target.id
  return attachBackendTargetAccessHeaders(targetConfig, target.id)
})

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response?.status === 401 &&
      error.config?.url !== '/auth/login' &&
      error.config?.url !== '/auth/config'
    ) {
      const requestTargetId = (error.config as BackendTargetRequestConfig | undefined)?.[
        BACKEND_TARGET_ID_CONFIG_KEY
      ]
      clearAccessToken(requestTargetId)
      if (!isOnLoginRoute()) {
        window.location.href = `${BASE_PATH}/login`
      }
    }
    return Promise.reject(error)
  }
)

export type { Repository } from '@/types'

export interface BackupOptions {
  archive_name?: string
}

export interface PruneOptions {
  keep_hourly?: number
  keep_daily?: number
  keep_weekly?: number
  keep_monthly?: number
  keep_quarterly?: number
  keep_yearly?: number
  keep_within?: string
  dry_run?: boolean
}

export interface CheckRepositoryOptions {
  maxDuration?: number
  checkExtraFlags?: string | null
}

function usesRepositoryDispatcher(data: Omit<Repository, 'id'>): boolean {
  return data.storage_backend === 'rclone_direct'
}

export class BorgApiClient {
  /** Version-aware URL prefix — the single routing decision point. */
  private readonly v: string

  /** Repository ID, carried for convenience on all calls. */
  readonly repoId: number
  private readonly repoPath?: string

  /** Whether this is a Borg 2 repo — needed for archive addressing even on the
   * v1 (agent) routes, where a Borg 2 archive series must be selected by id. */
  private readonly isV2: boolean

  constructor(repo: Repository) {
    this.repoId = repo.id
    this.repoPath = typeof repo.path === 'string' ? repo.path : undefined
    this.isV2 = isV2Repo(repo)
    this.v = repo.execution_target === 'agent' ? '' : this.isV2 ? '/v2' : ''
  }

  // ── Pre-creation (no repo ID yet) ────────────────────────────────────────

  static createRepository(data: Omit<Repository, 'id'>) {
    const v =
      data.execution_target === 'agent' || usesRepositoryDispatcher(data)
        ? ''
        : isV2Repo(data)
          ? '/v2'
          : ''
    return httpClient.post(`${v}/repositories/`, data)
  }

  static importRepository(data: Omit<Repository, 'id'>) {
    const v =
      data.execution_target === 'agent' || usesRepositoryDispatcher(data)
        ? ''
        : isV2Repo(data)
          ? '/v2'
          : ''
    return httpClient.post(`${v}/repositories/import`, data)
  }

  private get repoBase() {
    return `${this.v}/repositories/${this.repoId}`
  }

  // ── Repository info ──────────────────────────────────────────────────────

  getInfo() {
    return httpClient.get(`${this.repoBase}/info`)
  }

  // ── Archives ─────────────────────────────────────────────────────────────

  listArchives() {
    return httpClient.get(`${this.repoBase}/archives`)
  }

  getArchiveInfo(archiveId: string, includeFiles = false, fileLimit = 1000) {
    return httpClient.get(`${this.v}/archives/${archiveId}/info`, {
      params: { repository: this.repoId, include_files: includeFiles, file_limit: fileLimit },
    })
  }

  getArchiveContents(archiveId: string, archiveName: string, path = '', jobId?: number) {
    if (this.v === '/v2') {
      return httpClient.get(`/v2/archives/${archiveId}/contents`, {
        params: { repository: this.repoId, path },
      })
    }
    // v1 browse (cached, path-filtered). For a Borg 2 archive series the name is
    // ambiguous (matches N archives → borg errors), so select the specific
    // archive by its id via the `aid:` selector; Borg 1 names are unique.
    // Agent-backed archives are listed asynchronously: the first call may return
    // 202 + a jobId, which the caller passes back here to poll the running job.
    const selector = this.isV2 && archiveId ? `aid:${archiveId}` : archiveName
    return httpClient.get(`/browse/${this.repoId}/${encodeURIComponent(selector)}`, {
      params: { path, ...(jobId != null ? { job_id: jobId } : {}) },
    })
  }

  deleteArchive(archiveId: string) {
    return httpClient.delete(`${this.v}/archives/${archiveId}`, {
      params: { repository: this.repoId },
    })
  }

  getDeleteJobStatus(jobId: number) {
    return httpClient.get(`${this.v}/archives/delete-jobs/${jobId}`)
  }

  /**
   * Fetch a single file from an archive as a Blob via XHR (so HTTP errors are
   * catchable and can be surfaced in the UI). The Bearer header set by the
   * request interceptor authenticates; `get_current_download_user` accepts it.
   */
  fetchArchiveFile(
    archiveId: string,
    filePath: string,
    options?: { onDownloadProgress?: (event: AxiosProgressEvent) => void }
  ) {
    return httpClient.get(`${this.v}/archives/download`, {
      params: {
        repository: this.repoId,
        archive: archiveId,
        file_path: filePath,
      },
      responseType: 'blob',
      onDownloadProgress: options?.onDownloadProgress,
    })
  }

  // ── Backup operations ────────────────────────────────────────────────────

  runBackup(options: BackupOptions = {}) {
    if (this.v === '/v2') {
      return httpClient.post('/v2/backup/run', {
        repository_id: this.repoId,
        ...options,
      })
    }

    return httpClient.post('/backup/start', {
      repository: this.repoPath,
      ...options,
    })
  }

  pruneArchives(options: PruneOptions = {}) {
    if (this.v === '/v2') {
      return httpClient.post(`/v2/backup/prune`, { repository_id: this.repoId, ...options })
    }
    return httpClient.post(`/repositories/${this.repoId}/prune`, options)
  }

  compact() {
    if (this.v === '/v2') {
      return httpClient.post(`/v2/backup/compact`, { repository_id: this.repoId })
    }
    return httpClient.post(`/repositories/${this.repoId}/compact`)
  }

  checkRepository(optionsOrMaxDuration?: number | CheckRepositoryOptions) {
    const options =
      typeof optionsOrMaxDuration === 'number'
        ? { maxDuration: optionsOrMaxDuration }
        : optionsOrMaxDuration || {}
    const payload = {
      ...(options.maxDuration !== undefined && { max_duration: options.maxDuration }),
      ...(options.checkExtraFlags !== undefined && { check_extra_flags: options.checkExtraFlags }),
    }

    if (this.v === '/v2') {
      return httpClient.post(`/v2/backup/check`, {
        repository_id: this.repoId,
        ...payload,
      })
    }
    return httpClient.post(`/repositories/${this.repoId}/check`, payload)
  }
}
